import { useState, useEffect } from 'react';
import { Card, CardContent } from "@repo/o37-ui";
import { Building, Users, Tag, TrendingUp, ArrowUpDown } from "lucide-react";
import {
  formatCents,
  getMonthlyEquivalent,
  HoldingsBillingCycle
} from '../../types';

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

type ViewType = 'category' | 'company' | 'person';

interface CostItem {
  name: string;
  monthlyAmount: number;
  itemCount: number;
  itemType: 'subscription' | 'payroll' | 'mixed';
  percentage: number;
}

export default function CostsByView() {
  const [viewType, setViewType] = useState<ViewType>('category');
  const [data, setData] = useState<CostItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<'amount' | 'name'>('amount');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [totalMonthly, setTotalMonthly] = useState(0);

  const userId = localStorage.getItem('app_user_id') || '';
  const userRole = localStorage.getItem('app_role') || 'user';

  useEffect(() => {
    loadData();
  }, [viewType]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load subscriptions and compensation in parallel
      const [subsResponse, compResponse] = await Promise.all([
        fetch(`${API_URL}/holdings/subscriptions`, {
          headers: { 'x-user-id': userId, 'x-user-role': userRole }
        }),
        fetch(`${API_URL}/holdings/compensation?status=active`, {
          headers: { 'x-user-id': userId, 'x-user-role': userRole }
        })
      ]);

      if (!subsResponse.ok) {
        setData([]);
        return;
      }

      const subscriptions = await subsResponse.json();
      const compensations = compResponse.ok ? await compResponse.json() : [];
      const activeSubscriptions = subscriptions.filter((s: any) => s.status === 'active');

      // Calculate totals for percentage
      const subscriptionTotal = activeSubscriptions.reduce((sum: number, s: any) => {
        return sum + getMonthlyEquivalent(s.cost_cents, s.billing_cycle as HoldingsBillingCycle);
      }, 0);
      const compensationTotal = compensations.reduce((sum: number, c: any) => {
        return sum + Math.round((c.total_annual_cost_cents || 0) / 12);
      }, 0);
      const total = subscriptionTotal + compensationTotal;
      setTotalMonthly(total);

      if (viewType === 'category') {
        const grouped: Record<string, { amount: number; count: number; type: 'subscription' | 'payroll' | 'mixed' }> = {};

        // Group subscriptions by category
        activeSubscriptions.forEach((s: any) => {
          const cat = s.category || 'Uncategorized';
          if (!grouped[cat]) grouped[cat] = { amount: 0, count: 0, type: 'subscription' };
          grouped[cat].amount += getMonthlyEquivalent(s.cost_cents, s.billing_cycle as HoldingsBillingCycle);
          grouped[cat].count += 1;
        });

        // Add Payroll category for compensation
        if (compensations.length > 0) {
          grouped['Payroll'] = {
            amount: compensationTotal,
            count: compensations.length,
            type: 'payroll'
          };
        }

        const items: CostItem[] = Object.entries(grouped).map(([name, data]) => ({
          name,
          monthlyAmount: data.amount,
          itemCount: data.count,
          itemType: data.type,
          percentage: total > 0 ? (data.amount / total) * 100 : 0
        }));

        setData(items);
      } else if (viewType === 'company' || viewType === 'person') {
        // Need to load seat assignments
        const seatPromises = activeSubscriptions.map(async (s: any) => {
          const seatRes = await fetch(`${API_URL}/holdings/subscriptions/${s.id}`, {
            headers: { 'x-user-id': userId, 'x-user-role': userRole }
          });
          if (seatRes.ok) {
            const subData = await seatRes.json();
            return {
              subscription: s,
              seats: subData.holdings_subscription_seats || []
            };
          }
          return { subscription: s, seats: [] };
        });

        const subscriptionsWithSeats = await Promise.all(seatPromises);

        const grouped: Record<string, { amount: number; count: number; type: 'subscription' | 'payroll' | 'mixed' }> = {};

        // Add subscription costs from seat assignments
        subscriptionsWithSeats.forEach(({ subscription, seats }) => {
          const monthlyPerSeat = subscription.total_seats > 0
            ? getMonthlyEquivalent(subscription.cost_cents, subscription.billing_cycle as HoldingsBillingCycle) / subscription.total_seats
            : 0;

          seats.forEach((seat: any) => {
            let name = 'Unassigned';
            if (viewType === 'company' && seat.holdings_companies) {
              name = seat.holdings_companies.name;
            } else if (viewType === 'person' && seat.holdings_people) {
              name = seat.holdings_people.name;
            } else if (viewType === 'company' && seat.company_id) {
              name = `Company ${seat.company_id.slice(0, 8)}`;
            } else if (viewType === 'person' && seat.person_id) {
              name = `Person ${seat.person_id.slice(0, 8)}`;
            }

            if (!grouped[name]) grouped[name] = { amount: 0, count: 0, type: 'subscription' };
            grouped[name].amount += monthlyPerSeat * (seat.seats_assigned || 1);
            grouped[name].count += 1;
          });
        });

        // Add compensation costs
        if (viewType === 'company') {
          // Group compensation by company
          compensations.forEach((c: any) => {
            const companyName = c.company?.name || c.holdings_companies?.name || 'Unknown Company';
            const monthlyComp = Math.round((c.total_annual_cost_cents || 0) / 12);

            if (!grouped[companyName]) {
              grouped[companyName] = { amount: 0, count: 0, type: 'payroll' };
            } else if (grouped[companyName].type === 'subscription') {
              grouped[companyName].type = 'mixed';
            }
            grouped[companyName].amount += monthlyComp;
            grouped[companyName].count += 1;
          });
        } else if (viewType === 'person') {
          // Group compensation by person
          compensations.forEach((c: any) => {
            const personName = c.person?.name || c.holdings_people?.name || 'Unknown Person';
            const monthlyComp = Math.round((c.total_annual_cost_cents || 0) / 12);

            if (!grouped[personName]) {
              grouped[personName] = { amount: 0, count: 0, type: 'payroll' };
            } else if (grouped[personName].type === 'subscription') {
              grouped[personName].type = 'mixed';
            }
            grouped[personName].amount += monthlyComp;
            grouped[personName].count += 1;
          });
        }

        const items: CostItem[] = Object.entries(grouped).map(([name, data]) => ({
          name,
          monthlyAmount: data.amount,
          itemCount: data.count,
          itemType: data.type,
          percentage: total > 0 ? (data.amount / total) * 100 : 0
        }));

        setData(items);
      }
    } catch (e) {
      console.error('Failed to load cost data:', e);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const sortedData = [...data].sort((a, b) => {
    if (sortBy === 'amount') {
      return sortDir === 'desc' ? b.monthlyAmount - a.monthlyAmount : a.monthlyAmount - b.monthlyAmount;
    }
    return sortDir === 'desc' ? b.name.localeCompare(a.name) : a.name.localeCompare(b.name);
  });

  const toggleSort = (field: 'amount' | 'name') => {
    if (sortBy === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
  };

  const views: { id: ViewType; label: string; icon: React.ReactNode }[] = [
    { id: 'category', label: 'By Category', icon: <Tag className="w-4 h-4" /> },
    { id: 'company', label: 'By Company', icon: <Building className="w-4 h-4" /> },
    { id: 'person', label: 'By Person', icon: <Users className="w-4 h-4" /> }
  ];

  // Color palette for bars
  const colors = [
    'bg-blue-500',
    'bg-emerald-500',
    'bg-purple-500',
    'bg-amber-500',
    'bg-rose-500',
    'bg-cyan-500',
    'bg-indigo-500',
    'bg-orange-500',
    'bg-teal-500',
    'bg-pink-500'
  ];

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold mb-1">Cost Analytics</h1>
        <p className="text-neutral-500">Analyze costs by category, company, or person</p>
      </div>

      {/* View Selector */}
      <div className="flex items-center gap-1 p-1 bg-neutral-100 rounded-lg w-fit mb-6">
        {views.map((view) => (
          <button
            key={view.id}
            onClick={() => setViewType(view.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              viewType === view.id
                ? 'bg-white text-neutral-900 shadow-sm'
                : 'text-neutral-600 hover:text-neutral-900'
            }`}
          >
            {view.icon}
            {view.label}
          </button>
        ))}
      </div>

      {/* Summary */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-neutral-500 mb-1">Total Monthly Run Rate</div>
              <div className="text-3xl font-bold text-neutral-900">{formatCents(totalMonthly)}</div>
            </div>
            <div className="flex items-center gap-2 text-neutral-500">
              <TrendingUp className="w-5 h-5" />
              <span>{data.length} {viewType === 'category' ? 'categories' : viewType === 'company' ? 'companies' : 'people'}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Table/Chart */}
      <Card>
        <CardContent className="p-6">
          {loading ? (
            <div className="text-center py-12 text-neutral-500">Loading...</div>
          ) : sortedData.length === 0 ? (
            <div className="text-center py-12 text-neutral-500">
              <Tag className="w-12 h-12 mx-auto mb-4 text-neutral-300" />
              <p>No cost data available</p>
            </div>
          ) : (
            <>
              {/* Sort Controls */}
              <div className="flex items-center gap-4 mb-4">
                <button
                  onClick={() => toggleSort('amount')}
                  className={`flex items-center gap-1 text-sm ${sortBy === 'amount' ? 'text-neutral-900 font-medium' : 'text-neutral-500'}`}
                >
                  <ArrowUpDown className="w-3.5 h-3.5" />
                  Sort by Amount
                </button>
                <button
                  onClick={() => toggleSort('name')}
                  className={`flex items-center gap-1 text-sm ${sortBy === 'name' ? 'text-neutral-900 font-medium' : 'text-neutral-500'}`}
                >
                  <ArrowUpDown className="w-3.5 h-3.5" />
                  Sort by Name
                </button>
              </div>

              {/* Bar Chart */}
              <div className="space-y-4">
                {sortedData.map((item, index) => (
                  <div key={item.name} className="group">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${colors[index % colors.length]}`} />
                        <span className="font-medium text-neutral-900">{item.name}</span>
                        <span className="text-sm text-neutral-400">
                          ({item.itemCount} {item.itemType === 'payroll' ? (item.itemCount !== 1 ? 'people' : 'person') : item.itemType === 'mixed' ? 'items' : (item.itemCount !== 1 ? 'subs' : 'sub')})
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-medium text-neutral-900">{formatCents(item.monthlyAmount)}</span>
                        <span className="text-sm text-neutral-500 w-16 text-right">
                          {item.percentage.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                    <div className="h-2 bg-neutral-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${colors[index % colors.length]} transition-all group-hover:opacity-80`}
                        style={{ width: `${item.percentage}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
